import { IRunningTotalsResult, IViolationCache, IViolationResult } from "@vmosolutions/oc-interfaces";
import { PairingShader } from "./shaders/PairingShader";
import { Camera } from "../../../SharedGL/Camera";
import { SelectedDayGL } from "../../FlightTimeline/gl/SelectedDay/SelectedDayGL";
import { IRGBA } from "../../FlightTimeline/TimelineUtils";
import { TextureShader } from "./shaders/TextureShader";
import { CrewScheduleErrorsGL } from "./CrewScheduleErrorsGL";
import { ICrewViolations } from "src/redux/reducers/CrewScheduleReducer";
import moment from "moment-timezone";
import { BaseTimelineGL } from "src/SharedGL/BaseTimelineGL";

const pColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  pColors.push(...[0.0, 0.0, 0.0, 1.0]); //[0.54, 0.55, 0.52, 1.0]);
});

interface IViolationVertexOffset {
  [violationId: string]: {
    offset: number;
    vertexCount: number;
  };
}

interface IRunningTotalGL {
  time: number;
  label: string;
  yIndex: number;
  priority: RunningTotalStatus;
}

enum RunningTotalStatus {
  Normal,
  Warning,
  Error,
}
const violationColor = [1.0, 0.0, 0.0, 1.0];
const runningTotalBackgroundColor = "rgba(32, 32, 32, 0.2)";
export class CrewViolationsGL extends CrewScheduleErrorsGL {
  // private violationVertextOffset: IViolationVertexOffset = {};
  private violationData: IViolationCache;

  private readonly selectedWindowColor: IRGBA = {
    red: 0.0,
    green: 0.0,
    blue: 0.0,
    alpha: 0.55,
  };

  private violationWindow?: [number, number];
  private selectedWindowGL: SelectedDayGL;

  private runningTotals: any = {
    "700.27": [],
    "700.29": [],
  };

  private longestLabel = "";

  private _flatViolationResults: IViolationResult[] = [];

  public createdAt = 0;

  /**
   * Creates instance of CrewViolationsGL
   * @param gl - WebGL rendering context
   * @param shader - the webGL shader to render the crew error
   * @param textureShader - the webGL Shader to render error days "X" repeating texture
   * @param texture - the webGL texture containing the repeating "X" texture
   */
  constructor(gl: WebGLRenderingContext, shader: PairingShader, textureShader: TextureShader, texture: WebGLTexture) {
    super(gl, shader, textureShader, texture);
    this.selectedWindowGL = new SelectedDayGL(gl);
  }

  /**
   * getViolationData - the cached violation data
   */
  public getViolationData = (): IViolationCache => this.violationData;

  /**
   * @param dayStartMs - start of day in ms
   * @param dayEndMs - end of day in ms
   * @returns true if any crew violation touches the day
   */
  public hasOnDay(dayStartMs: number, dayEndMs: number): boolean {
    return (
      this._flatViolationResults?.some(
        (v) => v.status === "fail" && !v.noShow && v.period && v.period.end >= dayStartMs && v.period.start <= dayEndMs
      ) ?? false
    );
  }

  /**
   * updateViolationBuffers - Updates the webGL buffers for violation errors
   * @param gl - The WebGL rendering context
   * @param violations - Violation error map to be rendered
   * @param rowHeight - the row height of the crew member to draw day violations
   */
  public updateViolationBuffers(
    gl: WebGLRenderingContext,
    violations: ICrewViolations,
    rowHeight: number,
    createdAt: number
  ): void {
    const crewViolationWindow = Object.keys(violations).map((yearMonth: string) =>
      moment.utc(yearMonth, "YYYYMM")?.valueOf()
    );
    const min = Math.min(...crewViolationWindow);
    const max = moment
      .utc(Math.max(...crewViolationWindow))
      .endOf("month")
      ?.valueOf();

    this.violationWindow = [min, max];
    const violationResults: IViolationResult[] = Object.values(violations).flatMap(
      ({ regulationResults }) => regulationResults
    );

    // cache for hasOnDay
    this._flatViolationResults = violationResults.filter(
      (v) => v && v.status === "fail" && !v.noShow && v.period && v.period.start != null && v.period.end != null
    );

    this.createdAt = createdAt ?? 0;
    // Update the running total results to display Drop off hours
    Object.values(violations).forEach(({ runningTotalResults }) => {
      return runningTotalResults.forEach((runningTotalResult: IRunningTotalsResult, yIndex: number) => {
        if (!this.runningTotals[runningTotalResult.type]) {
          this.runningTotals[runningTotalResult.type] = [];
        }

        const result = Object.keys(runningTotalResult.map).forEach((timeKey: string) => {
          const runningTotal = Object.values((runningTotalResult.map as { [key: string]: any })[timeKey]);
          runningTotal.sort((a: any, b: any) => a.daySpan - b.daySpan);
          const time = new Date(timeKey)?.valueOf();

          const runningTotalGL: IRunningTotalGL = {
            time,
            yIndex,
            label: runningTotal
              .map((val: any) => `${(val.maxHours - val.workedHours).toFixed(1)}`)
              .join("/")
              .replace(/\s/g, ""),
            priority: runningTotal.some((val: any) => (val.maxHours - val.workedHours) / val.maxHours < 0.2)
              ? RunningTotalStatus.Warning
              : RunningTotalStatus.Normal,
          };
          this.runningTotals[runningTotalResult.type].push(runningTotalGL);

          this.longestLabel =
            this.longestLabel.length > runningTotalGL.label.length ? this.longestLabel : runningTotalGL.label;
        });
        return result;
      });
    });
    super.updateBuffers(gl, violationResults, rowHeight, violationColor);
  }

  /**
   * draw - draws the violation errors in webGL
   * @param gl - The WebGL rendering context
   * @param camera - the camera to get dimensions of visible window
   * @param yStart - y dimension of crewMember row start
   */
  public draw(gl: WebGLRenderingContext, camera: Camera, yStart: number): void {
    super.draw(gl, camera, yStart, -5.5);
  }

  /**
   *
   * @param canvasCtx - the 2D Canvas context to render labels
   * @param camera - the camera to get dimensions of visible window
   * @param width - width of the canvas
   * @param yStart - y dimension of crewMember row start
   * @param hideDropOffHours - whether or not to hide/show drop off hours
   */
  public drawRunningTotals(
    canvasCtx: CanvasRenderingContext2D,
    camera: Camera,
    width: number,
    yStart: number,
    hideDropOffHours = false
  ): void {
    if (!this.runningTotals) {
      return;
    }
    const [minX, maxX] = camera.getXRange();
    const [minY] = camera.getYRange();
    const onlyShowWarnings = hideDropOffHours || moment(maxX).diff(moment(minX), "days") > 7;

    canvasCtx.save();
    canvasCtx.textAlign = "left";
    canvasCtx.textBaseline = "bottom";
    canvasCtx.fillStyle = "white";
    canvasCtx.font = "normal 9px 'B612 Mono'";

    const startingY = yStart - minY + BaseTimelineGL.timelineStart;
    const y0 = startingY;

    const textWidth = canvasCtx.measureText(this.longestLabel).width;
    Object.values(this.runningTotals).forEach((value: any) => {
      for (const runningTotal of value) {
        if (onlyShowWarnings && runningTotal.priority === RunningTotalStatus.Normal) {
          continue;
        }

        if (runningTotal.time >= minX && runningTotal.time <= maxX) {
          const pixelY = y0 - runningTotal.yIndex * 9;
          const pixelX = camera.timeAxisToPixelCoords(runningTotal.time, width);
          canvasCtx.fillStyle = runningTotalBackgroundColor;
          canvasCtx.fillRect(pixelX - 2, pixelY - 10, textWidth + 4, 10);
          canvasCtx.fillStyle = runningTotal.priority === RunningTotalStatus.Warning ? "yellow" : "white";

          canvasCtx.fillText(runningTotal.label, pixelX, pixelY);
        }
      }
    });
    canvasCtx.restore();
  }
}
